import React, { useEffect, useRef, useState } from "react";
import API from "../../api/api";

const emptyForm = { title: "", category: "MYAS Disclosure", sort_order: 0, is_active: true };

export default function ManagePublicDocuments() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    try {
      const response = await API.get("/admin/public-documents", { params: { all: true } });
      setRows(response?.data?.data || response?.data || response || []);
    } catch (_error) {
      setRows([]);
    }
  };

  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm(emptyForm);
    setFile(null);
    setEditing(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!editing && !file) return;
    setBusy(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([key, value]) => payload.append(key, typeof value === "boolean" ? (value ? "1" : "0") : value));
      if (file) payload.append("file", file);
      if (editing) {
        await API.post(`/admin/public-documents/${editing.id}`, payload, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await API.post("/admin/public-documents", payload, { headers: { "Content-Type": "multipart/form-data" } });
      }
      reset();
      load();
    } finally {
      setBusy(false);
    }
  };

  const edit = (row) => {
    setEditing(row);
    setForm({ title: row.title || "", category: row.category || "MYAS Disclosure", sort_order: row.sort_order || 0, is_active: Boolean(row.is_active) });
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.title}”? This also removes its uploaded file.`)) return;
    await API.delete(`/admin/public-documents/${row.id}`);
    if (editing?.id === row.id) reset();
    load();
  };

  return <main className="p-5 md:p-8 max-w-6xl mx-auto">
    <header className="rounded-2xl bg-gradient-to-r from-emerald-800 to-teal-700 px-6 py-5 text-white shadow-sm">
      <p className="text-xs font-black uppercase tracking-[.18em] text-emerald-100">Admin Workspace</p>
      <h1 className="mt-1 text-3xl font-black">MYAS Disclosure</h1>
      <p className="mt-1 text-emerald-50">Upload, edit, publish or remove public governance and compliance documents.</p>
    </header>
    <form onSubmit={submit} className="mt-7 grid md:grid-cols-2 gap-4 rounded-2xl border bg-white p-5 shadow-sm">
      <input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Document title" className="border rounded-lg px-3 py-2" />
      <input required value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} placeholder="Category" className="border rounded-lg px-3 py-2" />
      <input ref={fileInput} required={!editing} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(event) => setFile(event.target.files?.[0] || null)} className="border rounded-lg px-3 py-2" />
      <input type="number" min="0" value={form.sort_order} onChange={(event) => setForm({ ...form, sort_order: event.target.value })} placeholder="Display order" className="border rounded-lg px-3 py-2" />
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} /> Published publicly</label>
      <div className="flex gap-3"><button disabled={busy} className="rounded-lg bg-emerald-700 text-white font-bold px-4 py-2 disabled:opacity-50">{busy ? "Saving…" : editing ? "Save Changes" : "Upload & Publish"}</button>{editing ? <button type="button" onClick={reset} className="rounded-lg border px-4 py-2 font-bold">Cancel</button> : null}</div>
    </form>
    <div className="mt-7 overflow-x-auto rounded-2xl border bg-white"><table className="w-full text-sm"><thead className="bg-slate-50 text-left"><tr><th className="p-3">Document</th><th className="p-3">Category</th><th className="p-3">Order</th><th className="p-3">Status</th><th className="p-3" /></tr></thead><tbody>{rows.map((row) => <tr key={row.id} className="border-t"><td className="p-3 font-semibold">{row.title}<span className="block text-xs text-slate-500">{row.file_name}</span></td><td className="p-3">{row.category}</td><td className="p-3">{row.sort_order}</td><td className="p-3">{row.is_active ? "Published" : "Hidden"}</td><td className="p-3 text-right whitespace-nowrap"><a href={row.file_url} target="_blank" rel="noreferrer" className="text-emerald-700 font-bold mr-3">View</a><button type="button" onClick={() => edit(row)} className="text-blue-700 font-bold mr-3">Edit</button><button type="button" onClick={() => remove(row)} className="text-rose-700 font-bold">Delete</button></td></tr>)}</tbody></table></div>
  </main>;
}
